import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { buildAppLink } from "../../../utils/landingRedirect";

type WorkflowId = "record" | "import" | "agent" | "create" | "study";

type WorkflowItem = {
  labelKey: string;
  icon: string;
};

type Workflow = {
  id: WorkflowId;
  icon: string;
  imageSrc?: string;
  workingIcon: string;
  workingImageSrc?: string;
  sources: WorkflowItem[];
  outputKeys: string[];
};

const WORKFLOWS: Workflow[] = [
  {
    id: "record",
    icon: "ion:mic-outline",
    workingIcon: "ion:mic-outline",
    sources: [
      { labelKey: "meetingAudio", icon: "ion:mic-outline" },
      { labelKey: "transcript", icon: "ion:musical-notes-outline" },
      { labelKey: "voiceNote", icon: "ion:language-outline" },
    ],
    outputKeys: ["notes", "tasks", "followUps"],
  },
  {
    id: "import",
    icon: "ion:cloud-upload-outline",
    workingIcon: "ion:cloud-upload-outline",
    sources: [
      { labelKey: "pdf", icon: "ion:document-text-outline" },
      { labelKey: "link", icon: "ion:link-outline" },
      { labelKey: "image", icon: "ion:image-outline" },
    ],
    outputKeys: ["summary", "citations", "contextBundle"],
  },
  {
    id: "agent",
    icon: "ion:hardware-chip-outline",
    imageSrc: "/logo/logo.png",
    workingIcon: "ion:hardware-chip-outline",
    workingImageSrc: "/logo/logo.png",
    sources: [
      { labelKey: "task", icon: "ion:checkbox-outline" },
      { labelKey: "context", icon: "ion:folder-open-outline" },
      { labelKey: "approval", icon: "ion:shield-checkmark-outline" },
    ],
    outputKeys: ["nextTask", "contextBundle", "action"],
  },
  {
    id: "create",
    icon: "ion:videocam-outline",
    workingIcon: "ion:videocam-outline",
    sources: [
      { labelKey: "source", icon: "ion:key-outline" },
      { labelKey: "brief", icon: "ion:videocam-outline" },
      { labelKey: "draft", icon: "ion:create-outline" },
    ],
    outputKeys: ["podcastOutline", "videoScript", "writingDraft"],
  },
  {
    id: "study",
    icon: "ion:book-outline",
    workingIcon: "ion:school-outline",
    sources: [
      { labelKey: "question", icon: "ion:help-circle-outline" },
      { labelKey: "source", icon: "ion:language-outline" },
      { labelKey: "answer", icon: "ion:reader-outline" },
    ],
    outputKeys: ["explanation", "translation", "answer"],
  },
];

const renderAppIcon = (
  icon: string,
  className: string,
  imageSrc?: string,
  imageClassName = className
) => {
  if (imageSrc) {
    return <img alt="" aria-hidden="true" className={imageClassName} src={imageSrc} />;
  }

  return <Icon icon={icon} className={className} />;
};

const WorkflowShowcase: React.FC = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<WorkflowId>("import");
  const activeWorkflow = useMemo(
    () => WORKFLOWS.find((workflow) => workflow.id === activeId) ?? WORKFLOWS[1],
    [activeId]
  );
  const showcaseKey = "modern.workflowShowcase";
  const activeWorkflowKey = `${showcaseKey}.workflows.${activeWorkflow.id}`;
  const text = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback });
  const workflowLabel = (workflowId: WorkflowId) =>
    text(`${showcaseKey}.tabs.${workflowId}`, workflowId);

  return (
    <section
      id="agent-enterprise"
      className="relative w-full scroll-mt-20 overflow-hidden bg-[#f8fbff] px-3 py-8 font-inter md:px-5 lg:py-10"
    >
      <div className="mx-auto max-w-[1240px] rounded-[28px] border border-[#dbe5f0] bg-white shadow-[0_24px_64px_rgba(78,164,245,0.16)]">
        <div className="grid min-h-[620px] grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] xl:min-h-[660px]">
          <aside className="border-b border-[#dbe5f0] p-5 lg:border-b-0 lg:border-r xl:p-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <h2 className="m-0 max-w-[116px] text-[15px] font-black uppercase leading-[1.18] text-[#6d7d91] xl:text-[16px]">
                {text(`${showcaseKey}.chooseWorkflow`, "Choose a workflow")}
              </h2>
              <div className="rounded-full border border-[#dbe5f0] px-3.5 py-2 text-[13px] font-black text-[#34527a] xl:text-[14px]">
                {text(`${showcaseKey}.hoverOrTap`, "Hover or tap")}
              </div>
            </div>

            <div className="grid gap-3 xl:gap-4">
              {WORKFLOWS.map((workflow) => {
                const isActive = workflow.id === activeWorkflow.id;
                return (
                  <button
                    key={workflow.id}
                    className={`flex h-[70px] items-center gap-3 rounded-[20px] border bg-white px-4 text-left transition-all xl:h-[76px] ${
                      isActive
                        ? "border-[#4ea4f5] shadow-[0_14px_32px_rgba(78,164,245,0.14)]"
                        : "border-transparent hover:border-[#c9def4] hover:bg-[#f8fbff]"
                    }`}
                    onClick={() => setActiveId(workflow.id)}
                    onMouseEnter={() => setActiveId(workflow.id)}
                    type="button"
                  >
                    <span className="grid h-[44px] w-[44px] flex-none place-items-center rounded-[14px] bg-[#eaf5ff] text-[#4ea4f5] xl:h-[48px] xl:w-[48px]">
                      {renderAppIcon(
                        workflow.icon,
                        "h-6 w-6 xl:h-7 xl:w-7",
                        workflow.imageSrc,
                        "h-7 w-7 object-contain xl:h-8 xl:w-8"
                      )}
                    </span>
                    <span className="min-w-0 flex-1 text-[18px] font-black text-[#1f2937] xl:text-[20px]">
                      {workflowLabel(workflow.id)}
                    </span>
                    <Icon icon="ion:arrow-forward-outline" className="h-5 w-5 text-[#8aa4c2]" />
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="bg-[#f8fbff] p-4 md:p-5 xl:p-6">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="h-full overflow-hidden rounded-[30px] border border-[#d6e1eb] bg-white shadow-[0_18px_46px_rgba(17,24,39,0.08)]"
              initial={{ opacity: 0, y: 10 }}
              key={activeWorkflow.id}
              transition={{ duration: 0.22 }}
            >
              <div className="flex h-[64px] items-center border-b border-[#dbe5f0] px-4 xl:h-[70px] xl:px-5">
                <div className="flex gap-2.5">
                  <span className="h-3 w-3 rounded-full bg-[#dce8f5]" />
                  <span className="h-3 w-3 rounded-full bg-[#dce8f5]" />
                  <span className="h-3 w-3 rounded-full bg-[#dce8f5]" />
                </div>
                <div className="mx-auto flex items-center gap-2 rounded-full border border-[#c7def5] bg-[#eaf5ff] px-4 py-2 text-[15px] font-black text-[#34527a] xl:text-[16px]">
                  <img alt="" aria-hidden="true" className="h-5 w-5 object-contain" src="/logo/logo.png" />
                  {text(`${showcaseKey}.workspace`, "YouWo Workspace")}
                </div>
                <div className="min-w-[64px] text-right text-[16px] font-black text-[#34527a] xl:min-w-[74px] xl:text-[17px]">
                  {workflowLabel(activeWorkflow.id)}
                </div>
              </div>

              <div className="grid min-h-[556px] grid-cols-1 lg:grid-cols-[190px_minmax(0,1fr)_210px] xl:min-h-[590px] xl:grid-cols-[205px_minmax(0,1fr)_225px]">
                <div className="border-b border-[#dbe5f0] p-4 lg:border-b-0 lg:border-r xl:p-5">
                  <h3 className="mb-4 mt-0 text-[15px] font-black uppercase text-[#6d7d91] xl:text-[16px]">
                    {text(`${showcaseKey}.sources`, "Sources")}
                  </h3>
                  <div className="grid gap-3 xl:gap-4">
                    {activeWorkflow.sources.map((source, index) => (
                      <div
                        className={`flex h-[70px] items-center gap-3 rounded-[18px] border px-3 xl:h-[78px] xl:px-4 ${
                          index === 0
                            ? "border-[#9ecfff] bg-white"
                            : "border-[#e1e6ec] bg-white"
                        }`}
                        key={source.labelKey}
                      >
                        <span className="grid h-[42px] w-[42px] flex-none place-items-center rounded-[14px] bg-[#eaf5ff] text-[#4ea4f5] xl:h-[46px] xl:w-[46px]">
                          <Icon icon={source.icon} className="h-6 w-6 xl:h-7 xl:w-7" />
                        </span>
                        <span className="truncate text-[16px] font-black text-[#1f2937] xl:text-[17px]">
                          {text(`${activeWorkflowKey}.sources.${source.labelKey}`, source.labelKey)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <main className="grid grid-rows-[1fr_auto] gap-4 p-4 xl:p-5">
                  <div className="rounded-[24px] border border-[#dbe5f0] bg-[#fbfdff] p-5 xl:p-6">
                    <div className="mb-4 flex items-center gap-2.5 text-[13px] font-black uppercase text-[#6d7d91] xl:mb-5 xl:text-[14px]">
                      {renderAppIcon(
                        activeWorkflow.workingIcon,
                        "h-5 w-5 text-[#4ea4f5]",
                        activeWorkflow.workingImageSrc,
                        "h-5 w-5 object-contain"
                      )}
                      {text(`${showcaseKey}.workingOn`, "Working on")}
                    </div>
                    <h3 className="m-0 max-w-[620px] text-[28px] font-black leading-[1.08] text-[#20293a] xl:text-[34px]">
                      {text(`${activeWorkflowKey}.hero`, "Turn files into usable context.")}
                    </h3>
                    <p className="mt-4 max-w-[620px] text-[15px] leading-[1.45] text-[#6d7d91] xl:text-[16px]">
                      {text(
                        `${activeWorkflowKey}.body`,
                        "Drop in files, links, images, audio, or video. YouWo makes them searchable and agent-ready."
                      )}
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 rounded-[22px] border border-[#dbe5f0] bg-white p-4 xl:flex-row xl:items-center xl:gap-4 xl:p-5">
                    <span className="grid h-[52px] w-[52px] flex-none place-items-center rounded-[16px] bg-[#eaf5ff] text-[#4ea4f5] xl:h-[58px] xl:w-[58px]">
                      {renderAppIcon(
                        activeWorkflow.icon,
                        "h-7 w-7 xl:h-8 xl:w-8",
                        activeWorkflow.imageSrc,
                        "h-8 w-8 object-contain xl:h-9 xl:w-9"
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-black uppercase text-[#6d7d91] xl:text-[15px]">
                        {text(`${showcaseKey}.nextStep`, "Next step")}
                      </div>
                      <div className="mt-1.5 text-[18px] font-black text-[#1f2937] xl:text-[20px]">
                        {text(`${activeWorkflowKey}.nextTitle`, "Prepare context")}
                      </div>
                      <p className="m-0 mt-1.5 max-w-[430px] text-[14px] leading-[1.35] text-[#7a8aa0] xl:text-[15px]">
                        {text(
                          `${activeWorkflowKey}.nextBody`,
                          "The source becomes useful for answers, drafts, and agents."
                        )}
                      </p>
                    </div>
                    <a
                      className="inline-flex h-[46px] shrink-0 items-center justify-center rounded-full border border-[#b9d9f9] bg-[#edf7ff] px-4 text-[14px] font-black text-[#34527a] no-underline transition-colors hover:bg-[#e2f1ff] xl:h-[50px] xl:px-5 xl:text-[15px]"
                      href={buildAppLink()}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {text(`${showcaseKey}.approveNextStep`, "Approve next step")}
                    </a>
                  </div>
                </main>

                <aside className="flex flex-col justify-between border-t border-[#dbe5f0] p-4 lg:border-l lg:border-t-0 xl:p-5">
                  <div className="rounded-[22px] border border-[#dbe5f0] bg-white p-4 xl:p-5">
                    <h3 className="m-0 mb-4 text-[15px] font-black uppercase text-[#6d7d91] xl:text-[16px]">
                      {text(`${showcaseKey}.readyOutput`, "Ready output")}
                    </h3>
                    <div className="grid gap-3.5">
                      {activeWorkflow.outputKeys.map((outputKey) => (
                        <div className="flex items-center gap-3" key={outputKey}>
                          <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[#e5f8ed] text-[#23a864]">
                            <Icon icon="ion:checkmark-outline" className="h-[18px] w-[18px]" />
                          </span>
                          <span className="text-[16px] font-black leading-tight text-[#1f2937] xl:text-[17px]">
                            {text(`${activeWorkflowKey}.outputs.${outputKey}`, outputKey)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <a
                    className="mt-6 inline-flex h-[52px] max-w-full items-center justify-center whitespace-nowrap rounded-full bg-[#345f91] px-4 text-[13px] font-black text-white no-underline transition-colors hover:bg-[#2d527d] xl:h-[56px] xl:text-[14px]"
                    href={buildAppLink()}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {text(`${showcaseKey}.tryWithContent`, "Try it with your content")}
                  </a>
                </aside>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowShowcase;
